/**
 * Class definition for `wp-tabpanel` element
 *
 * @class WPTabpanel
 * @extends {HTMLElement}
 */
class WPTabpanel extends HTMLElement {
  constructor(self) {
    self = super(self);
    this.setAttribute('role', 'tabpanel');
    this.tabIndex = -1;
    this.collapsed = true;
    this.animating = false;
    this._request = null;
    this._loading = false;
    return self;
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-tabpanel';
  }

  /**
   * Watched attributes
   *
   * @readonly
   * @static
   */
  static get observedAttributes() {
    return ['collapsed'];
  }

  get collapsed() {
    return this.hasAttribute('collapsed');
  }

  set collapsed(value) {
    if (Boolean(value)) {
      this.setAttribute('collapsed', '');
    } else {
      this.removeAttribute('collapsed');
    }
  }

  /**
   * Getter for `animating` attribute
   */
  get animating() {
    return this.hasAttribute('animating');
  }

  /**
   * Setter for `animating` attribute
   */
  set animating(value) {
    if (Boolean(value)) {
      this.setAttribute('animating', '');
    } else {
      this.removeAttribute('animating');
    }
  }

  /**
   * Getter for `loaded` attribute
   */
  get loaded() {
    return this.hasAttribute('loaded');
  }

  /**
   * Setter for `loaded` attribute
   */
  set loaded(value) {
    if (Boolean(value)) {
      this.setAttribute('loaded', '');
    } else {
      this.removeAttribute('loaded');
    }
  }

  /**
   * Getter for `src` attribute
   */
  get src() {
    return this.hasAttribute('src') ? this.getAttribute('src') : '';
  }

  /**
   * Setter for `src` attribute
   */
  set src(value) {
    if (String(value.length)) {
      this.setAttribute('src', value);
    } else {
      this.removeAttribute('src');
    }
  }

  /**
   * Lifecycle event when watched attribute is changed
   *
   * @param {string} name Attribute name
   * @param {any} oldVal Old value of attribute
   * @param {any} newVal New value of attribute
   */
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'collapsed':
        this._collapsedChanged();
    }
  }

  /**
   * Called when `collapsed` attribute changed
   *
   * @private
   */
  _collapsedChanged() {
    // If not collapsed, load src
    if (!this.collapsed && this.src && !this.loaded) {
      this.load();
    }

    this._animate().then(() => {
      this.animating = false;
    });
  }

  /**
   * Add event listener to ensure transition ends
   *
   * @private
   * @returns {Promise} Resolves when transitions ends
   */
  _animate() {
    return new Promise((resolve, reject) => {
      this.animating = true;
      this.$once('transitionend', () => {
        resolve();
      });

      setTimeout(() => {
        resolve();
      }, 500);
    });
  }

  /**
   * Fetch dynamic content
   *
   * @static
   * @param {string} uri
   * @returns {Promise} Resolves response
   */
  static _getResults(uri) {
    return new Promise((resolve, reject) => {
      this._request = new XMLHttpRequest();
      const url = new URL(uri);

      this._request.open('POST', url.origin + url.pathname, true);
      this._request.setRequestHeader(
        'Content-type',
        'application/x-www-form-urlencoded',
      );
      this._request.onload = resolve;
      this._request.onerror = reject;
      this._request.send(url.search.substring(1));
    });
  }

  load() {
    if (!this._loading) {
      // Set loader
      this.innerHTML = WPTabpanel.LOADER;
      this._loading = true;
    } else {
      // Abort current request
      if (this._request) {
        this._request.abort();
      }
    }

    WPTabpanel._getResults(this.src).then((evt) => {
      // Set content
      this.innerHTML = evt.target.response;
      this.loaded = true;
      this._loading = false;

      this.$emit('tabpanelloaded');
      this.$emit('checkanimations');
    });
  }

  static get LOADER() {
    return `
        <div class="loader">
          <svg class="loader__icon" xmlns="http://www.w3.org/2000/svg" viewBox="21.73913043478261 21.73913043478261 43.47826086956522 43.47826086956522">
            <circle fill="transparent" cx="43.47826086956522" cy="43.47826086956522" r="20" stroke-width="3.4782608695652177" stroke-dasharray="125.664" stroke-dashoffset="125.66370614359172px" class="path"></circle>
          </svg>
        </div>
      `;
  }
}

customElements.define(WPTabpanel.is, WPTabpanel);
